import { useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';

/**
 * Auto logout hook - logs out user after specified minutes of inactivity
 * Also logs out when tab/window is closed
 * Tracks: mouse movement, clicks, key presses, scroll, touch events
 * 
 * @param {number} timeoutMinutes - Minutes of inactivity before logout (default: 10)
 * @param {function} onLogout - Callback function to execute on logout
 * @param {boolean} enabled - Whether the auto logout is enabled (default: true)
 */
export const useAutoLogout = (timeoutMinutes = 10, onLogout, enabled = true) => {
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  
  const TIMEOUT_MS = timeoutMinutes * 60 * 1000; // Convert to milliseconds
  const WARNING_BEFORE_MS = 60 * 1000; // Show warning 1 minute before logout

  const handleLogout = useCallback(() => {
    // Clear any existing timeouts
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    
    // Show logout message
    toast.info('You have been logged out due to inactivity', {
      duration: 5000,
    });
    
    // Execute logout callback
    if (onLogout) {
      onLogout();
    }
  }, [onLogout]);

  const showWarning = useCallback(() => {
    toast.warning('You will be logged out in 1 minute due to inactivity. Move your mouse or press a key to stay logged in.', {
      duration: 10000,
      id: 'auto-logout-warning',
    });
  }, []);

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    
    // Clear existing timeouts
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    
    // Dismiss any existing warning
    toast.dismiss('auto-logout-warning');
    
    if (!enabled) return;
    
    // Set warning timeout (1 minute before logout)
    warningTimeoutRef.current = setTimeout(() => {
      showWarning();
    }, TIMEOUT_MS - WARNING_BEFORE_MS);
    
    // Set logout timeout
    timeoutRef.current = setTimeout(() => {
      handleLogout();
    }, TIMEOUT_MS);
  }, [enabled, TIMEOUT_MS, WARNING_BEFORE_MS, handleLogout, showWarning]);

  useEffect(() => {
    if (!enabled) return;

    // Activity events to track
    const events = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
      'wheel'
    ];

    // Throttle the reset to avoid too many calls
    let throttleTimer = null;
    const throttledReset = () => {
      if (throttleTimer) return;
      throttleTimer = setTimeout(() => {
        resetTimer();
        throttleTimer = null;
      }, 1000); // Only reset once per second max
    };

    // Handle tab/window close - only clear on actual close, not refresh
    // We use a flag to distinguish between refresh and close
    let isRefreshing = false;
    
    const handleBeforeUnload = (event) => {
      // If it's a refresh, don't clear the token
      // The beforeunload fires for both refresh and close
      // We mark refreshes by checking if the page is being navigated to itself
      if (isRefreshing) return;
      
      // Note: We no longer clear localStorage here to preserve session on refresh
      // The session will timeout naturally via inactivity timer
    };
    
    // Track navigation type to detect refresh vs close
    const handleUnload = () => {
      // Only executed on actual page unload (not refresh if handled properly)
      // This is a backup - most session management should be via inactivity
    };

    // Handle visibility change - logout when tab becomes hidden for extended period
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // User switched tabs or minimized - start a shorter timeout
        // We'll log them out if they don't return within 5 minutes
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
        
        timeoutRef.current = setTimeout(() => {
          handleLogout();
        }, 5 * 60 * 1000); // 5 minutes when tab is hidden
      } else {
        // User returned to tab - reset normal timer
        resetTimer();
      }
    };

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, throttledReset, { passive: true });
    });
    
    // Add tab close/visibility handlers
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Initialize timer
    resetTimer();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, throttledReset);
      });
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      if (throttleTimer) clearTimeout(throttleTimer);
    };
  }, [enabled, resetTimer, handleLogout]);

  // Return function to manually reset timer if needed
  return { resetTimer, lastActivity: lastActivityRef.current };
};

export default useAutoLogout;
